"use client";

import * as React from "react";
import { styled, alpha } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import debounce from "lodash/debounce";
import { Box, List, ListItem, ListItemButton, Paper } from "@mui/material";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useThemeMode } from "@/contexts/ThemeContext";

// Componenta comuna de cautare clienti - folosita atat in search-ul din
// header (components/Layouts/TopNavbar/SearchForm.js, variant="header",
// selectare = navigare la /client/[id]) cat si inline, oriunde trebuie
// asociat un client la ceva (ex. pages/scrisori/[id].js, variant="inline",
// selectare = callback). Aceeasi logica de debounce + dropdown, un singur
// loc de intretinut.
const Search = styled("div", { shouldForwardProp: (prop) => prop !== "variant" })(({ theme, variant }) => ({
	position: "relative",
	borderRadius: 100,
	backgroundColor: variant === "inline" ? theme.palette.action.hover : alpha(theme.palette.common.white, 0.15),
	"&:hover": {
		backgroundColor: variant === "inline" ? theme.palette.action.selected : alpha(theme.palette.common.white, 0.25),
	},
	marginRight: 0,
	marginLeft: variant === "inline" ? 0 : "15px",
	width: "100%",
	[theme.breakpoints.up("sm")]: {
		marginRight: variant === "inline" ? 0 : theme.spacing(1),
		width: variant === "inline" ? "100%" : "auto",
	},
}));

const SearchIconWrapper = styled("div", { shouldForwardProp: (prop) => prop !== "variant" })(({ theme, variant }) => ({
	color: variant === "inline" ? theme.palette.text.secondary : "#757FEF",
	padding: theme.spacing(0, 2),
	height: "100%",
	position: "absolute",
	right: "0",
	pointerEvents: "none",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	zIndex: "5",
}));

const StyledInputBase = styled(InputBase, { shouldForwardProp: (prop) => prop !== "variant" && prop !== "isDarkMode" })(({ theme, variant, isDarkMode }) => ({
	color: variant === "inline" ? "inherit" : isDarkMode ? "#fff" : "inherit",
	width: "100%",
	"& .MuiInputBase-input": {
		backgroundColor: variant === "inline" ? "transparent" : isDarkMode ? "#1f2226" : "#F5F7FA",
		color: variant === "inline" ? "inherit" : isDarkMode ? "#fff" : "inherit",
		borderRadius: "30px",
		padding: theme.spacing(1.4, 0, 1.4, 2),
		paddingRight: `calc(1em + ${theme.spacing(4)})`,
		transition: theme.transitions.create("width"),
		width: "100%",
		...(variant !== "inline" && {
			[theme.breakpoints.up("sm")]: {
				width: "260px",
				"&:focus": {
					width: "280px",
				},
			},
		}),
	},
}));

const SuggestionsContainer = styled(Paper)(({ theme }) => ({
	position: "absolute",
	top: "100%",
	left: 0,
	right: 0,
	zIndex: 10,
	marginTop: theme.spacing(0.5),
	maxHeight: "300px",
	overflow: "auto",
	boxShadow: theme.shadows[3],
}));

export default function ClientSearchAutocomplete({
	onSelectClient,
	placeholder = "Caută clienți...",
	variant = "header",
	minChars = 3,
}) {
	const [searchTerm, setSearchTerm] = React.useState("");
	const [suggestions, setSuggestions] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [openSuggestions, setOpenSuggestions] = React.useState(false);

	const { isDarkMode } = useThemeMode();

	const fetchSuggestions = React.useMemo(
		() =>
			debounce(async (searchValue) => {
				if (searchValue.length > minChars - 1) {
					setLoading(true);
					try {
						const params = new URLSearchParams({
							page: 1,
							pageSize: 10,
							search: searchValue,
						});

						const response = await fetchWrapper.get(`/api/admin/users2?${params}`);

						setSuggestions(response.data || []);
						setOpenSuggestions(true);
					} catch (error) {
						console.error("Eroare căutare clienți:", error);
						setSuggestions([]);
					} finally {
						setLoading(false);
					}
				} else {
					setSuggestions([]);
					setOpenSuggestions(false);
				}
			}, 300),
		[minChars]
	);

	React.useEffect(() => {
		return () => {
			fetchSuggestions.cancel();
		};
	}, [fetchSuggestions]);

	const handleSearchChange = (event) => {
		const value = event.target.value;
		setSearchTerm(value);
		fetchSuggestions(value);
	};

	const handleClientSelect = (client) => {
		setOpenSuggestions(false);
		setSearchTerm("");
		onSelectClient(client);
	};

	const handleInputBlur = () => {
		setTimeout(() => {
			setOpenSuggestions(false);
		}, 200);
	};

	return (
		<Box sx={{ position: "relative", width: variant === "inline" ? "100%" : "auto" }}>
			<Search className='client-search-form' variant={variant}>
				<SearchIconWrapper variant={variant} sx={{ display: variant === "inline" ? "flex" : { xs: "none", sm: "inline-flex" } }}>
					<SearchIcon />
				</SearchIconWrapper>

				<StyledInputBase
					variant={variant}
					isDarkMode={isDarkMode}
					placeholder={placeholder}
					inputProps={{ "aria-label": "search" }}
					value={searchTerm}
					onChange={handleSearchChange}
					onFocus={() => searchTerm.length > minChars - 1 && setOpenSuggestions(true)}
					onBlur={handleInputBlur}
				/>
			</Search>

			{openSuggestions && (
				<SuggestionsContainer style={{ backgroundColor: isDarkMode ? "#0d1015" : "white" }}>
					{loading ? (
						<ListItem>Se caută...</ListItem>
					) : suggestions.length > 0 ? (
						<List>
							{suggestions.map((client) => (
								<ListItem key={client.id} disablePadding>
									<ListItemButton onClick={() => handleClientSelect(client)}>
										{[client.firstName, client.middleName, client.lastName].filter(Boolean).join(" ")}
									</ListItemButton>
								</ListItem>
							))}
						</List>
					) : (
						<ListItem>Niciun client găsit</ListItem>
					)}
				</SuggestionsContainer>
			)}
		</Box>
	);
}
